import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Nav, Footer } from './LandingPage';

const DISPLAY = 'font-display font-extrabold tracking-tighter';
const BODY = 'font-body';
const METALLIC = 'text-transparent bg-clip-text bg-gradient-to-br from-gray-900 via-gray-600 to-gray-900';

const FACTS = [
  ['Product', 'LinkMonkey'],
  ['Category', 'Link-in-bio tool / creator landing page'],
  ['Website', 'https://linkmonkey.io'],
  ['Best for', 'Creators, musicians, streamers, artists, podcasters, small businesses'],
  ['Free plan', 'Yes — free forever, no card required'],
  ['Paid plan', 'Pro — €4/month or €40/year (€3.33/mo), 7-day free trial'],
  ['Tip commission', '0% — tips go directly to the creator\u2019s PayPal'],
  ['Setup time', 'About 2 minutes from signup to live page'],
  ['Contact', 'hello@linkmonkey.io'],
];

const FEATURES = [
  '63 real brand icons with official colours',
  'Rich inline embeds — YouTube, Spotify, SoundCloud, Apple Music, Bandcamp, TikTok and more',
  'Zero-commission tip jar with direct PayPal payouts',
  'Fan page — an algorithm-free, Bebo-style updates wall',
  'Visitor analytics with clicks per link and a country-level map',
  'Branded QR code generator (PNG + SVG)',
  'Timed links that appear and disappear automatically',
  'Email subscribers and campaigns',
  '17 themes, 12 fonts, 11 wallpapers, animations and custom accents',
  'Affiliate program with recurring commission',
];

const SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About LinkMonkey',
  url: 'https://linkmonkey.io/about',
  mainEntity: {
    '@type': 'Organization',
    '@id': 'https://linkmonkey.io/#org',
    name: 'LinkMonkey',
    url: 'https://linkmonkey.io/',
    email: 'hello@linkmonkey.io',
    description: 'LinkMonkey is an all-in-one link-in-bio tool and affordable Linktree alternative for creators. One shareable page with rich embeds, a 0%-commission tip jar, fan page, analytics, QR codes and full customisation. Free plan forever; Pro from €3.33/month.',
  },
};

export default function About() {
  return (
    <div className="min-h-screen bg-[#D9E9FC] text-gray-950 antialiased selection:bg-gray-950 selection:text-white" data-testid="about-page">
      <Helmet>
        <title>About LinkMonkey — The All-in-One Link in Bio Tool</title>
        <meta name="description" content="What LinkMonkey is, who it's for, what it costs and every feature included: rich embeds, 0%-commission tip jar, fan page, analytics, QR codes and more." />
        <link rel="canonical" href="https://linkmonkey.io/about" />
        <script type="application/ld+json">{JSON.stringify(SCHEMA)}</script>
        <link rel="preconnect" href="https://api.fontshare.com" crossOrigin="anonymous" />
        <link href="https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@400,500,700,800,900,400i&f[]=satoshi@400,500,700&display=swap" rel="stylesheet" />
      </Helmet>

      <Nav />

      <section className="relative pt-36 md:pt-44 pb-14 px-6 md:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <p className={`${BODY} text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-5`}>About</p>
          <h1 className={`${DISPLAY} text-[clamp(2.6rem,7vw,5rem)] leading-[0.95] mb-6`}>
            <span className={`block ${METALLIC}`}>One link. Every fan.</span>
            <span className="block italic font-light tracking-tight text-[#0055FF]">No nickel-and-diming.</span>
          </h1>
          <p className={`${BODY} text-gray-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto`}>
            LinkMonkey exists because creators shouldn't pay extra for basics. Every feature —
            embeds, tips, analytics, QR codes, themes — is included in one simple plan, and the
            tip jar takes 0% of what your fans send you.
          </p>
        </div>
      </section>

      {/* Facts block — structured, quotable */}
      <section className="relative pb-16 px-6 md:px-10">
        <div className="max-w-3xl mx-auto rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden" data-testid="about-facts">
          <div className="px-6 py-4 border-b border-gray-200 bg-[#F1F7FF]">
            <h2 className={`${DISPLAY} text-gray-900 text-xl tracking-tight`}>LinkMonkey at a glance</h2>
          </div>
          <dl>
            {FACTS.map(([k, v], i) => (
              <div key={k} className={`grid grid-cols-[130px_1fr] sm:grid-cols-[180px_1fr] gap-4 px-6 py-3.5 ${i % 2 ? 'bg-[#F7FAFF]' : ''}`}>
                <dt className={`${BODY} text-xs uppercase tracking-wider text-gray-500 pt-0.5`}>{k}</dt>
                <dd className={`${BODY} text-sm text-gray-800`}>{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Feature list */}
      <section className="relative pb-16 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <h2 className={`${DISPLAY} text-[clamp(1.6rem,3.5vw,2.4rem)] leading-tight mb-8`}>
            <span className={METALLIC}>Everything included.</span>
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="about-features">
            {FEATURES.map((f) => (
              <li key={f} className={`${BODY} flex items-start gap-2.5 text-sm text-gray-700 rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3`}>
                <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Story */}
      <section className="relative pb-20 px-6 md:px-10">
        <div className={`${BODY} max-w-3xl mx-auto rounded-2xl border border-gray-200 bg-white shadow-sm p-8 text-gray-700 text-[15px] leading-relaxed space-y-4`}>
          <h2 className={`${DISPLAY} text-gray-900 text-2xl tracking-tight`}>Why we built it</h2>
          <p>
            Link-in-bio tools became the front door of the creator economy — and then started charging
            for every doorknob. Analytics behind one tier, themes behind another, a cut of your tips on top.
          </p>
          <p>
            LinkMonkey is the counter-offer: a beautiful creator page where the useful things are simply
            included. Free to start, €3.33 a month if you want it all, and your fans' support goes to you — not us.
          </p>
          <p>
            See it in action on our <Link to="/examples" className="text-[#0055FF] font-semibold hover:underline">examples page</Link>,
            or compare us honestly on the <Link to="/linktree-alternative" className="text-[#0055FF] font-semibold hover:underline">Linktree alternative</Link> page.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 px-6 md:px-10 bg-[#06070C] text-center overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 55% at 50% 100%, rgba(0,85,255,0.18) 0%, transparent 65%)' }} />
        <div className="relative">
          <h2 className={`${DISPLAY} text-[clamp(2rem,5vw,3.5rem)] leading-[0.95] mb-5 text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/40`}>
            Own your stage.
          </h2>
          <p className={`${BODY} text-white/60 mb-8`}>Your page can be live before this song ends.</p>
          <Link to="/signup">
            <button className={`${BODY} inline-flex items-center gap-2 bg-[#0055FF] text-white text-sm font-semibold px-7 py-4 rounded-full hover:bg-[#3377FF] transition-colors shadow-[0_4px_14px_rgba(0,85,255,0.3)] hover:scale-[1.02]`}
              data-testid="about-cta">
              Create your free page
              <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
